import { Link } from "react-router-dom";
import { BadgeEuro, CheckCircle2, ShieldCheck } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { Hero } from "@/components/site/Hero";
import { PageShell } from "@/components/site/PageShell";
import { CtaBand, FaqAccordion, SectionHeading, Steps } from "@/components/sections/Sections";
import { SERVICES } from "@/data/services";
import { breadcrumbLd, faqLd, serviceLd } from "@/lib/jsonld";
import { ServiceIcon } from "@/lib/icons";

export default function ServicePage({ service: s }) {
  const crumbs = [{ label: s.name, to: `/${s.slug}` }];
  const others = SERVICES.filter((x) => x.slug !== s.slug);

  return (
    <>
      <Seo title={s.title} description={s.description} path={`/${s.slug}`} jsonLd={[serviceLd(s), faqLd(s.faq), breadcrumbLd(crumbs)]} />
      <PageShell hero={<Hero image={s.image} imageAlt={s.imageAlt} imagePosition={s.imagePosition} eyebrow={s.name} title={s.h1} subtitle={s.subtitle} breadcrumbs={crumbs} />}>
        <div className="space-y-16" data-testid={`service-page-${s.slug}`}>
          <section>
            <p className="eyebrow mb-3">Die Ausgangslage</p>
            <h2 className="h2-brand text-2xl sm:text-3xl">{s.problem.heading}</h2>
            {s.problem.paragraphs.map((p) => <p key={p} className="mt-4 leading-relaxed">{p}</p>)}
          </section>

          <section className="rounded-3xl border border-slate-200 p-6 sm:p-8">
            <p className="eyebrow mb-3">Unsere Lösung</p>
            <h2 className="h2-brand text-2xl sm:text-3xl">{s.solution.heading}</h2>
            <p className="mt-4 leading-relaxed">{s.solution.text}</p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {s.solution.bullets.map((b) => (
                <li key={b} className="flex gap-2.5 text-[15px] font-semibold text-brand-anthracite">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-blue" /> {b}
                </li>
              ))}
            </ul>
          </section>

          <section>
            <SectionHeading eyebrow="Ablauf" title="So läuft es ab" />
            <Steps items={s.steps} prefix="service-steps" />
          </section>

          <section className="rounded-3xl border-l-4 border-brand-yellow bg-brand-mist p-6 sm:p-8" data-testid="service-costs">
            <BadgeEuro className="h-7 w-7 text-brand-blue" />
            <h2 className="mt-3 text-2xl font-bold text-brand-anthracite">{s.costs.heading}</h2>
            {s.costs.paragraphs.map((p) => <p key={p} className="mt-3 leading-relaxed">{p}</p>)}
          </section>

          <section>
            <SectionHeading eyebrow="Vertrauen" title="Darum Kfz-Sachverständiger Marcone" />
            <ul className="grid gap-3 sm:grid-cols-2">
              {s.trust.map((t) => (
                <li key={t} className="flex gap-3 rounded-2xl bg-white p-5 shadow-soft ring-1 ring-slate-100">
                  <ShieldCheck className="h-5 w-5 shrink-0 text-brand-blue" />
                  <span className="text-[15px] font-semibold text-brand-anthracite">{t}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <SectionHeading eyebrow="FAQ" title={`Häufige Fragen: ${s.name}`} />
            <FaqAccordion items={s.faq} prefix="service" />
          </section>

          <section>
            <p className="mb-4 text-sm font-bold uppercase tracking-wider text-brand-blue">Weitere Leistungen</p>
            <ul className="flex flex-wrap gap-2">
              {others.map((o) => (
                <li key={o.slug}>
                  <Link to={`/${o.slug}`} data-testid={`related-service-${o.slug}`}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-brand-anthracite transition-colors hover:border-brand-blue hover:text-brand-blue">
                    <ServiceIcon name={o.icon} className="h-4 w-4" /> {o.name}
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <CtaBand prefix="service-cta" />
        </div>
      </PageShell>
    </>
  );
}
